import axios, { addInterceptors } from './interceptor-axios';
import { localStorage } from '~/services';

const skyImageNames = [
  '05_falling_star.jpg',
  '1_stars.jpg',
  '2_stars.jpg',
  '03_stars.jpg',
  '04_stars.jpg',
  '06_falling_star.jpg',
  '07_Aurora.jpg',
  '08_aurora.jpg',
  '09_Aurora.jpg',
  '10_Aurora.jpg',
  '11_clear_sky.jpg',
  '11_clear.jpg',
  '12_clear.jpg',
  '13_clear.jpg',
  '14_clear.jpg',
  '15_clear.jpg',
  '17_clear.jpg',
  '18_clear_sunrise.jpg',
  '19_clear_sunrise.jpg',
  '20_sunrise.jpg',
  '21_sunrise.jpg',
  '22_sunrise.jpg',
  '23_sunrise.jpg',
  '24_sunrise.jpg',
  '25_sunset.jpg',
  '26_sunset.jpg',
  '27_magic.jpg',
  '28_sunset.jpg',
  '29_sunset.jpg',
  '30_sunrise.jpg',
  '31_rainbow.jpg',
  '32_rainbow.jpg',
  '33_rainbow.jpg',
  '34_rainbow.jpg',
  '35_milky way.jpg',
  '36_milky way.jpg',
  '37_milky way.jpg',
  '38_stars_timelaps.jpg',
  '39_stars_Timelapse.jpg',
  '40_stars_timelaps.jpg',
  '41_stars_timelaps.jpg',
  '42_stormy-coulds.jpg',
  '43_Stars_milky way.jpg',
  '44_Stars.jpg',
  '45_galaxy.jpg',
  '46_galaxy.jpg',
  '47_falling_Stars.jpg',
  '48_Milky way.jpg',
  '49_lightning.jpg',
  '50_lightning.jpg',
  '51_lightning.jpg',
  '52_lightning.jpg',
  '53_lightning.jpg',
  '54_RAINBOW.jpg',
  '15200_l.jpg',
  '15201_l.jpg',
  '15212_l.jpg',
  '1612_l.jpg',
  '1622_l.jpg',
  '1640_l.jpg',
  '17186_l.jpg',
  '2289223_l.jpg',
  '22972_l.jpg',
  '28698_l.jpg',
  '28701_l.jpg',
  '30125_l.jpg',
  '30205_l.jpg',
  '34871_l.jpg',
  '34956_l.jpg',
  '41757_l.jpg',
  '44714_l.jpg',
  '48253_l.jpg',
  '53102_l.jpg',
  '56921_l.jpg',
  '59566_l.jpg',
  '59567_l.jpg',
  '66148_l.jpg',
  '66149_l.jpg',
  '66153_l.jpg',
  '66158_l.jpg',
  '68176_l.jpg',
  '79933_l.jpg',
  '8386_l.jpg',
  '8388_l.jpg',
  '8423_l.jpg',
  '86914_l.jpg',
  '96181_l.jpg',
];

const API_SORAAC_PRESIGNED_UPLOAD = 'https://kaoac.com/api/sora/upload';

class AIService {
  constructor(token) {
    const headers = {};
    if (token) {
      headers.Authorization = token;
    }
    this.axios = axios.create({
      headers,
    });
    addInterceptors(this.axios);
  }

  getStyleImages = ({ page, maxResults }) => {
    const request = {
      method: 'GET',
      url: 'https://api.ac-artist.com/style-images',
      params: {
        page,
        max_results: maxResults || 45,
        service_type: 'photo_ac',
      },
    };
    return this.axios(request);
  };

  getSkyImages = ({ page, maxResults }) => {
    const uploadedSkies = localStorage.uploadedSkies;

    let skiesObjects = uploadedSkies
      .slice(0)
      .reverse()
      .map((sky) => ({
        id: sky.key,
        key: sky.key,
        thumbnail: sky.url,
      }));

    let startIdx = (page - 1) * (maxResults - skiesObjects.length);
    if (startIdx < 0) startIdx = 0;
    const imgNames = skyImageNames.slice(
      startIdx,
      startIdx + (maxResults - skiesObjects.length),
    );
    let skies = {
      images: [...skiesObjects].concat(
        imgNames.map((name) => ({
          id: name,
          key: `data/sora/${name}`,
          thumbnail: `https://ac-sora.s3-ap-northeast-1.amazonaws.com/data/thumbnails/${name}`,
        })),
      ),
      page,
    };
    return skies;
  };

  uploadBlobToSkyAC = async (blobObj) => {
    const request = {
      method: 'POST',
      url: API_SORAAC_PRESIGNED_UPLOAD,
      data: {
        fileName: 'editorAC-file.png',
        contentType: 'image/png',
      },
    };
    const res = await this.axios(request);
    const { key_name, signedUrl } = res;
    await fetch(signedUrl, {
      method: 'PUT',
      body: blobObj,
    });
    return key_name;
  };

  uploadFileToSkyAC = async (file) => {
    const request = {
      method: 'POST',
      url: API_SORAAC_PRESIGNED_UPLOAD,
      data: {
        fileName: file.name,
        contentType: file.type,
      },
    };
    const res = await this.axios(request);
    const { key_name, signedUrl } = res;
    await fetch(signedUrl, {
      method: 'PUT',
      body: file,
    });
    localStorage.uploadedSkies = [...localStorage.uploadedSkies].concat({
      key: key_name,
      url: signedUrl.split('?AWSAccessKeyId')[0],
    });
    return key_name;
  };

  processSkyAC = async ({ skyUrl, landUrl }) => {
    const data = {
      sky: skyUrl,
      land: landUrl,
      channel: 0,
      thresh: 255,
      enhance: false,
      preview: false,
    };
    const request = {
      method: 'POST',
      url: 'https://api.kakudai-ac.com/ac-sora',
      data,
    };
    return await this.axios(request);
  };

  uploadBlobToClippingAC = async (blobObj) => {
    const request = {
      method: 'POST',
      url:
        'https://wwxxnba9vh.execute-api.ap-northeast-1.amazonaws.com/dev/generate-upload-url',
      data: {
        files: [
          {
            filename: 'editorAC-file.png',
            content_type: 'image/png',
          },
        ],
      },
    };
    const res = await this.axios(request);
    const { object_url, upload_url } = res[0];
    await fetch(upload_url, {
      method: 'PUT',
      body: blobObj,
    });
    return object_url;
  };

  processClippingAC = async (image_url) => {
    const data = {
      image_url,
      size: 'full',
    };
    const request = {
      method: 'POST',
      url: 'https://api.kakudai-ac.com/api-kirinuki',
      data,
    };
    return await this.axios(request);
  };

  uploadToKakudaiAC = async (blobObj) => {
    const request = {
      method: 'POST',
      url:
        'https://v80cl17ng3.execute-api.ap-northeast-1.amazonaws.com/dev/upload-url',
      data: {
        files: [
          {
            filename: 'editorAC-file.png',
            content_type: 'image/png',
          },
        ],
      },
    };
    const res = await this.axios(request);
    const { original_url, upload_url } = res[0];
    await fetch(upload_url, {
      method: 'PUT',
      body: blobObj,
    });
    return original_url;
  };

  submitKakudaiAC = async ({ image, scale, dejpeg, deblur }) => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('scale', scale);
    formData.append('dejpeg', dejpeg);
    formData.append('deblur', deblur);

    return fetch('https://api-ai-backup.photo-ac.com/api-kakudai-kirei', {
      method: 'POST',
      body: formData,
    });
  };
}

export default new AIService();
